import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, MapPin, ShieldCheck } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../lib/i18n";
import { waLink, PHONE_TEL, PHONE_DISPLAY, pick } from "../lib/api";

export default function Footer({ services = [] }) {
    const { t, lang } = useLang();
    return (
        <footer id="contact" className="bg-forest-950 text-forest-100 mt-0" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <Logo dark />
                        <p className="mt-5 text-forest-200/80 leading-relaxed max-w-sm">{t.footer.about}</p>
                        <div className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-forest-900/80 border border-forest-800 px-4 py-3" data-testid="footer-trust-badge">
                            <ShieldCheck size={22} className="text-forest-300 shrink-0" />
                            <div className="text-sm leading-tight">
                                <div className="font-semibold text-white">{t.footer.registered}</div>
                                <div className="text-forest-300 text-xs tracking-wider">{t.footer.brn}</div>
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-3">
                        <h3 className="font-display font-semibold text-white text-lg">{t.footer.services}</h3>
                        <ul className="mt-4 space-y-2.5">
                            {services.map((s) => (
                                <li key={s.slug}>
                                    <Link to={`/services/${s.slug}`} className="text-forest-200/80 hover:text-white transition-colors duration-200 text-sm" data-testid={`footer-service-${s.slug}`}>
                                        {pick(s, "name", lang)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-4">
                        <h3 className="font-display font-semibold text-white text-lg">{t.footer.contact}</h3>
                        <ul className="mt-4 space-y-3 text-sm">
                            <li>
                                <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-3 text-forest-200/80 hover:text-white transition-colors" data-testid="footer-phone">
                                    <Phone size={16} className="text-forest-300" /> {PHONE_DISPLAY}
                                </a>
                            </li>
                            <li>
                                <a href={waLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-forest-200/80 hover:text-white transition-colors" data-testid="footer-whatsapp">
                                    <MessageCircle size={16} className="text-forest-300" /> WhatsApp {PHONE_DISPLAY}
                                </a>
                            </li>
                            <li className="flex items-start gap-3 text-forest-200/80">
                                <MapPin size={16} className="text-forest-300 mt-0.5 shrink-0" /> {t.footer.address}
                            </li>
                        </ul>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-wa-btn" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-forest-950 hover:brightness-110 transition-all">
                                <MessageCircle size={15} /> WhatsApp
                            </a>
                            <a href={`tel:${PHONE_TEL}`} data-testid="footer-call-btn" className="inline-flex items-center gap-2 rounded-full border border-forest-700 px-4 py-2 text-sm font-semibold text-white hover:bg-forest-900 transition-all">
                                <Phone size={15} /> {t.common.call}
                            </a>
                        </div>
                    </div>
                </div>
                <div className="mt-14 pt-7 border-t border-forest-900 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-forest-300/70">
                    <div>© {new Date().getFullYear()} X Terminate Ltd · {t.footer.rights}</div>
                    <div className="flex items-center gap-5">
                        <Link to="/packages" className="hover:text-white transition-colors">{t.footer.packages}</Link>
                        <Link to="/quote" className="hover:text-white transition-colors">{t.footer.quote}</Link>
                        <Link to="/admin" className="text-forest-300/40 hover:text-forest-200 transition-colors" data-testid="footer-admin-link">{t.footer.admin}</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}